import React, { useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import { Modal as ResponsiveModal } from 'react-responsive-modal';
import Image from 'ui3/Image/Image';
import Button from 'ui3/Button/Button';
import Text from 'ui3/Text/Text';
import { withCDN } from 'helpers/cdn';
import { openStoreOrAppUrl } from 'helpers/openAppUrl';
import { isAndroidUserAgent } from 'helpers/userAgent';
import {
  APPLE_APP_STORE_URL,
  GOOGLE_PLAY_STORE_URL,
  ANDROID_PACKAGE,
  APP_DEEP_LINK_SCHEME,
} from 'constants/external-links';
import 'react-responsive-modal/styles.css';
import styles from './OpenInAppModal.module.scss';

export type OpenInAppModalVariant = 'profile' | 'join';

type OpenInAppModalProps = {
  open: boolean;
  onClose: () => void;
  /** Callback for staying on web. If not provided, default behavior is to close modal. */
  onStayOnWeb?: () => void;
  variant?: OpenInAppModalVariant;
  username?: string;
  profileImageUrl?: string | null;
  /** When true (e.g. on live stream), redirect to store to download only; no deep link attempt. */
  redirectToStoreOnly?: boolean;
};

const PROFILE_TITLE = 'See Full Profile In The MILLIONS App';
const PROFILE_SUBTITLE = (username: string) =>
  `Use the app to see more about ${username || '@username'}`;
const JOIN_TITLE = 'Join In MILLIONS App';
const JOIN_SUBTITLE =
  'Claim your spot, connect with fans & unlock more features.';

const DEFAULT_APP_LOGO = '/images/logos/millions-3d-icon.png';
/** Join modal: circle logo. Use PNG so it works reliably in <img> (SVG had pattern ref issues). */
const JOIN_LOGO = '/images/millions-circle.png';

const OpenInAppModal = ({
  open,
  onClose,
  onStayOnWeb,
  variant = 'profile',
  username,
  profileImageUrl,
  redirectToStoreOnly = false,
}: OpenInAppModalProps): JSX.Element => {
  const isProfile = variant === 'profile';
  const title = isProfile ? PROFILE_TITLE : JOIN_TITLE;
  const subtitle = isProfile
    ? PROFILE_SUBTITLE(
        username ? `@${username.replace(/^@/, '')}` : '@username'
      )
    : JOIN_SUBTITLE;
  // Join modal = millions-circle; profile modal = profile image or 3d icon
  const logoSrc = isProfile
    ? profileImageUrl
      ? profileImageUrl.startsWith('http')
        ? profileImageUrl
        : withCDN(profileImageUrl)
      : withCDN(DEFAULT_APP_LOGO)
    : JOIN_LOGO;

  const storeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (storeTimeoutRef.current) {
        window.clearTimeout(storeTimeoutRef.current);
      }
    };
  }, []);

  const handleOpenApp = useCallback((): void => {
    if (typeof window === 'undefined') return;
    onClose();
    const isAndroid = isAndroidUserAgent();
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const path = window.location.pathname + window.location.search;
    const pathWithoutLeadingSlash = path.replace(/^\//, '');
    const deepLinkUrl = `${APP_DEEP_LINK_SCHEME}://${pathWithoutLeadingSlash}`;

    if (redirectToStoreOnly) {
      if (isAndroid) {
        openStoreOrAppUrl(GOOGLE_PLAY_STORE_URL, false);
      } else {
        openStoreOrAppUrl(APPLE_APP_STORE_URL, false);
      }
      return;
    }

    if (isAndroid) {
      const fallbackUrl = encodeURIComponent(GOOGLE_PLAY_STORE_URL);
      const url = `intent://${pathWithoutLeadingSlash}#Intent;scheme=${APP_DEEP_LINK_SCHEME};package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallbackUrl};end`;
      openStoreOrAppUrl(url, true);
    } else if (isIOS) {
      window.location.href = deepLinkUrl;
      storeTimeoutRef.current = window.setTimeout(() => {
        if (document.visibilityState === 'visible') {
          openStoreOrAppUrl(APPLE_APP_STORE_URL);
        }
      }, 2500);
    } else {
      openStoreOrAppUrl(APPLE_APP_STORE_URL);
    }
  }, [onClose, redirectToStoreOnly]);

  return (
    <>
      <ResponsiveModal
        center
        open={open}
        classNames={{
          overlay: styles.overlay,
          modalContainer: styles.centerWrapper,
          modal: styles.modalWrapper,
        }}
        onClose={onClose}
        showCloseIcon={false}
        focusTrapped
      >
        <div className={styles.modalContainer}>
          <div className={styles.modal}>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close"
            >
              <svg
                className={styles.closeIcon}
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6835 4.90237 18.3166 4.90237 18.7071 5.29289C19.0977 5.68342 19.0977 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3165 18.7071 18.7071C18.3165 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0977 5.68342 19.0977 5.29289 18.7071C4.90237 18.3166 4.90237 17.6835 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z"
                />
              </svg>
            </button>
            <div className={styles.entry}>
              <div
                className={cn(
                  styles.logoWrap,
                  isProfile ? styles.logoWrapProfile : styles.logoWrapJoin
                )}
              >
                {isProfile ? (
                  <Image src={logoSrc} alt="" className={styles.logo} />
                ) : (
                  <img src={JOIN_LOGO} alt="" className={styles.logo} />
                )}
              </div>

              <Text variant="h5" textAlign="center" className={styles.title}>
                {title}
              </Text>
              <Text
                variant="body2Regular"
                color="lights-medium"
                textAlign="center"
              >
                {subtitle}
              </Text>

              <div className={styles.actions}>
                <Button variant="primary" block onClick={handleOpenApp}>
                  Open App
                </Button>
                <Button
                  variant="primary"
                  subVariant="text"
                  block
                  onClick={() => (onStayOnWeb ? onStayOnWeb() : onClose())}
                >
                  Stay on Web
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default OpenInAppModal;
